export function getDaysInMonth(year: number, monthIndexZeroBased: number): number {
  return new Date(year, monthIndexZeroBased + 1, 0).getDate();
}

export function getMonthAbbrs(locale = 'en-US'): string[] {
  return Array.from({ length: 12 }, (_, i) =>
    new Intl.DateTimeFormat(locale, { month: 'short' }).format(new Date(2020, i, 1)),
  );
}

export function formatISOAsYearMonDay(iso: string, locale = 'en-US'): string {
  const [y, m, d] = iso.split('-').map(Number);
  const mon = new Intl.DateTimeFormat(locale, { month: 'short' }).format(new Date(2020, m - 1, 1));
  return `${y} ${mon} ${String(d).padStart(2, '0')}`;
}

/**
 * Formats an ISO date/time string as `dd.MM.yy` (e.g. `13.02.26`).
 * Returns `fallback` when value is empty/invalid.
 */
export function formatISOAsDayMonYearShortDots(
  value: string | null | undefined,
  fallback = '—',
  locale: string = 'en-GB',
): string {
  if (!value) return fallback;

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return fallback;

  return new Intl.DateTimeFormat(locale, {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
  })
    .format(parsed)
    .replace(/\//g, '.');
}

export function formatISOAsMonDay(
  value: string | null | undefined,
  fallback = '—',
  locale: string = 'en-US',
): string {
  if (!value) return fallback;

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return fallback;

  return new Intl.DateTimeFormat(locale, { month: 'short', day: 'numeric' }).format(parsed);
}

/**
 * Formats an ISO date/time string as `dd-MM-yyyy` (e.g. `13-02-2026`).
 * Returns `fallback` when value is empty/invalid.
 */
export function formatISOAsDayMonYearDashes(
  value: string | null | undefined,
  fallback = '—',
  locale: string = 'en-GB',
): string {
  if (!value) return fallback;

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return fallback;

  return new Intl.DateTimeFormat(locale, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  })
    .format(parsed)
    .replace(/\//g, '-');
}

/**
 * Formats an ISO date/time string as `Mon DD, YYYY` (e.g. `May 7, 2026`).
 * Returns null when the input is empty or invalid.
 */
export function formatISOAsLongDate(
  value: string | null | undefined,
  locale: string = 'en-US',
): string | null {
  if (!value) return null;

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;

  return new Intl.DateTimeFormat(locale, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(parsed);
}

/**
 * Returns true when the given date is more than `months` months before now.
 * Returns false when the value is empty or invalid.
 */
export function isOlderThanMonths(
  value: string | Date | null | undefined,
  months: number,
): boolean {
  if (!value) return false;

  const parsed = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(parsed.getTime())) return false;

  const threshold = new Date();
  threshold.setMonth(threshold.getMonth() - months);
  return parsed < threshold;
}

/**
 * Builds the tooltip text for a "Blocked" status badge, including the
 * blocked date and reason if available. Falls back to a date-less message otherwise.
 */
export function buildBlockedTooltip(
  subject: 'Fund' | 'Entity',
  blockedAt: string | null | undefined,
  blockReason?: string | null,
): string {
  const formatted = formatISOAsLongDate(blockedAt);
  const base = formatted
    ? `This verified ${subject} has been blocked by Gamma Prime on ${formatted}.`
    : `This verified ${subject} has been blocked by Gamma Prime.`;
  return blockReason ? `${base} Reason: ${blockReason}` : base;
}
